import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Play, Film, User, Calendar, Languages } from "lucide-react";
import { fetchMovie, fetchMe, overrideCountry } from "@/lib/api";
import { DEFAULT_MOVIE_DATA } from "@/lib/movieDefaults";
import PaywallDialog from "@/components/PaywallDialog";
import CountryPicker from "@/components/CountryPicker";
import AboutSindhionismDialog from "@/components/AboutSindhionismDialog";
import ContactDialog from "@/components/ContactDialog";

export default function Landing() {
  // Render immediately with bundled defaults; refresh from API in background.
  const [data, setData] = useState(DEFAULT_MOVIE_DATA);
  const [pricing, setPricing] = useState(DEFAULT_MOVIE_DATA.pricing);
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [auth, setAuth] = useState({
    signedIn: false,
    hasAccess: false,
    profileComplete: false,
    email: null,
    name: null,
    city: null,
    phone: null,
  });
  const navigate = useNavigate();

  const refreshAuth = () =>
    fetchMe()
      .then((res) =>
        setAuth({
          signedIn: true,
          hasAccess: !!res?.has_access,
          profileComplete: !!res?.profile_completed,
          email: res?.email || null,
          name: res?.name || null,
          city: res?.city || null,
          phone: res?.phone || null,
        }),
      )
      .catch(() =>
        setAuth({
          signedIn: false,
          hasAccess: false,
          profileComplete: false,
          email: null,
          name: null,
          city: null,
          phone: null,
        }),
      );

  const refreshMovie = () =>
    fetchMovie()
      .then((res) => {
        setData(res);
        if (res?.pricing) setPricing(res.pricing);
      })
      .catch(() => {});

  useEffect(() => {
    refreshMovie();
    refreshAuth();
  }, []);

  const handleWatch = () => {
    // If everything is ready, go straight to the player.
    if (auth.signedIn && auth.hasAccess && auth.profileComplete) {
      navigate("/watch");
      return;
    }
    // Open the dialog. If pricing isn't known yet (no country chosen), the
    // dialog opens at the REGION stage and forces selection before checkout.
    setOpen(true);
  };

  const handleFlowComplete = async () => {
    await refreshAuth();
    navigate("/watch");
  };

  const { movie } = data;

  return (
    <main className="min-h-screen text-[#F3F3F2] relative">
      {/* Top Nav */}
      <nav className="fixed top-0 left-0 right-0 z-40 px-4 sm:px-6 md:px-12 py-4 sm:py-6 flex items-center justify-between bg-gradient-to-b from-[#0A0A0A] to-transparent">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Film className="w-4 h-4 text-[#D4A373] shrink-0" strokeWidth={1.5} />
          <span className="font-display text-base sm:text-xl tracking-tight truncate">Byo Cha Khape</span>
        </div>
        <div className="flex items-center gap-4 sm:gap-6 text-[10px] sm:text-xs tracking-[0.2em] uppercase font-body text-[#A1A1AA]">
          <Link
            to="/login"
            data-testid="nav-login-link"
            className="hover:text-[#D4A373] transition-colors duration-300"
          >
            Sign In
          </Link>
          <button
            data-testid="nav-watch-btn"
            onClick={handleWatch}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 border border-[#27272A] hover:border-[#D4A373] hover:text-[#F3F3F2] transition-colors duration-300"
          >
            Watch <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative flex flex-col sm:block sm:min-h-screen">
        {/* Image — vertical 4:5 portrait on mobile, widescreen backdrop on desktop */}
        <div className="relative w-full aspect-[4/5] bg-[#0A0A0A] sm:aspect-auto sm:absolute sm:inset-0 overflow-hidden">
          <picture>
            {movie.poster_url_mobile && (
              <source media="(max-width: 639px)" srcSet={movie.poster_url_mobile} />
            )}
            <img
              src={movie.poster_url}
              alt={movie.title}
              width="1920"
              height="1080"
              className="poster-kenburns w-full h-full object-cover sm:object-[50%_25%]"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          {/* Desktop overlay gradients */}
          <div className="hidden sm:block absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-[#0A0A0A]/25" />
          <div className="hidden sm:block absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/55 via-transparent to-[#0A0A0A]/30" />
          {/* Mobile bottom fade into the text block below */}
          <div className="sm:hidden absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#0A0A0A] to-transparent pointer-events-none" />
        </div>

        <div className="relative z-10 w-full px-5 pt-8 pb-12 sm:px-6 md:px-12 sm:absolute sm:bottom-0 sm:left-0 sm:pt-0 sm:pb-20 md:pb-28 sm:max-w-5xl">
          <div className="fade-up delay-1 text-[#D4A373] text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-4 sm:mb-6 font-body">
            {movie.presented_by || "A Sindhi Film"} · {movie.year}
          </div>
          <h1
            className="fade-up delay-2 font-display text-5xl sm:text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter mb-4 sm:mb-6"
            data-testid="hero-title"
          >
            {movie.title}
          </h1>
          <p className="fade-up delay-3 font-display italic text-lg sm:text-2xl md:text-3xl text-[#F3F3F2]/80 max-w-2xl mb-8 sm:mb-10 leading-snug">
            {movie.tagline}
          </p>
          <div className="fade-up delay-4 flex flex-col sm:flex-row gap-3 sm:items-center">
            <button
              data-testid="hero-watch-btn"
              onClick={handleWatch}
              className="group inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-4 bg-[#D4A373] text-[#0A0A0A] hover:bg-[#E5B98D] active:bg-[#E5B98D] transition-colors duration-300 text-xs sm:text-sm tracking-[0.2em] uppercase font-semibold"
            >
              <Play className="w-4 h-4 fill-current" />
              Watch the Film
            </button>
            <span className="text-[#A1A1AA] font-body text-xs sm:text-sm" data-testid="hero-tagline">
              60 Days Access · Cinema Quality
            </span>
          </div>
        </div>
      </section>

      {/* Synopsis + Asymmetric grid */}
      <section className="cv-auto px-5 sm:px-6 md:px-12 py-16 sm:py-24 md:py-32 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-7">
            <div className="text-[#D4A373] text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-4 sm:mb-6 font-body">
              The Story
            </div>
            <p className="font-display text-2xl sm:text-3xl md:text-4xl leading-[1.3] text-[#F3F3F2]/90 max-w-2xl">
              {movie.synopsis}
            </p>
          </div>
          <div className="md:col-span-5 md:pl-8 md:border-l border-[#27272A] space-y-6 sm:space-y-8">
            <DetailRow icon={<User className="w-4 h-4" />} label="Director" value={movie.director} />
            {movie.producer && (
              <DetailRow icon={<Film className="w-4 h-4" />} label="Producer" value={movie.producer} />
            )}
            <DetailRow icon={<Calendar className="w-4 h-4" />} label="Runtime" value={`${movie.runtime} · ${movie.release_date || movie.year}`} />
            <DetailRow icon={<Languages className="w-4 h-4" />} label="Language" value={movie.language} />
            <DetailRow icon={<Film className="w-4 h-4" />} label="Cast" value={movie.cast.join(", ")} />
          </div>
        </div>
      </section>

      {/* Editorial split: image + ticket teaser */}
      <section className="cv-auto px-5 sm:px-6 md:px-12 pb-16 sm:pb-24 md:pb-32 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          <div className="md:col-span-7 relative overflow-hidden h-[300px] sm:h-[420px] md:h-[560px]">
            <img
              src={movie.still_url}
              alt="Cast still"
              className="w-full h-full object-cover object-top"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="md:col-span-5 flex flex-col justify-between border border-[#27272A] p-6 sm:p-8 md:p-12">
            <div>
              <div className="text-[#D4A373] text-[10px] sm:text-xs tracking-[0.3em] uppercase mb-4 sm:mb-6 font-body">
                Independent · Direct to You
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight leading-tight mb-4 sm:mb-6">
                No app. No subscription. Just the film.
              </h2>
              <p className="font-body text-[#A1A1AA] text-sm sm:text-base leading-relaxed">
                The complete Sindhi blockbuster — in Cinema Quality, with English subtitles. Pay once and watch on up to 2 devices for 60 days.
              </p>
            </div>
            <div className="mt-8 sm:mt-10 space-y-4">
              <div className="flex items-center" data-testid="hero-country-picker">
                <CountryPicker
                  countryCode={pricing?.country_code}
                  onChange={setPricing}
                  placeholder="Select Your Country"
                />
              </div>
              <button
                data-testid="cta-watch-btn"
                onClick={handleWatch}
                className="w-full mt-2 group inline-flex items-center justify-between gap-3 px-6 py-4 bg-[#D4A373] text-[#0A0A0A] hover:bg-[#E5B98D] active:bg-[#E5B98D] transition-colors duration-300 text-xs sm:text-sm tracking-[0.2em] uppercase font-semibold"
              >
                Watch the Film
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <div className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#52525B] font-body text-center">
                60 Days Access · Cinema Quality
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="cv-auto px-5 sm:px-6 md:px-12 py-10 sm:py-12 border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6">
          <div>
            <div className="font-display text-xl sm:text-2xl tracking-tight">Byo Cha Khape</div>
            <div className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mt-2 font-body">
              A Sindhionism Original · © {new Date().getFullYear()}
            </div>
          </div>
          <div className="flex items-center gap-6 sm:gap-8 text-[10px] sm:text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-body">
            <Link to="/credits" data-testid="footer-credits-link" className="hover:text-[#D4A373] transition-colors tracking-[0.2em] uppercase">Film Credits</Link>
            <button
              data-testid="footer-about-sindhionism-btn"
              onClick={() => setAboutOpen(true)}
              className="hover:text-[#D4A373] transition-colors tracking-[0.2em] uppercase"
            >
              About Sindhionism
            </button>
            <button
              data-testid="footer-contact-btn"
              onClick={() => setContactOpen(true)}
              className="hover:text-[#D4A373] transition-colors tracking-[0.2em] uppercase"
            >
              Contact
            </button>
            <Link to="/login" data-testid="footer-login-link" className="hover:text-[#D4A373] transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>

      <PaywallDialog
        open={open}
        onOpenChange={setOpen}
        pricing={pricing}
        onPricingChange={async (next, countryCode) => {
          // The CountryPicker / SwitcherButton already calls /api/geo/override and
          // returns the new pricing object. Just sync our state.
          if (next && next.price != null) {
            setPricing(next);
          } else if (countryCode) {
            try {
              const updated = await overrideCountry(countryCode);
              setPricing(updated);
            } catch { /* no-op */ }
          }
        }}
        auth={auth}
        onComplete={handleFlowComplete}
      />

      <AboutSindhionismDialog open={aboutOpen} onOpenChange={setAboutOpen} />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

const DetailRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="text-[#D4A373] mt-1">{icon}</div>
    <div>
      <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] font-body mb-1">{label}</div>
      <div className="font-body text-[#F3F3F2]">{value}</div>
    </div>
  </div>
);
